import React, {
  useEffect,
  useState,
  memo,
  forwardRef,
  useRef,
  useImperativeHandle
} from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { useAppContext } from '../../../providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import { FallingLines } from 'react-loader-spinner';
import axios from 'axios';
import dayjs from 'dayjs';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DataZoomComponent
]);

const getDefaultOptions = (theme, getThemeColor, data) => {
  const sortedData = data.sort((a, b) => new Date(a[0]) - new Date(b[0]));

  const values = sortedData.map(item => item[1]); // Extract the values (ignoring timestamps)
  const dynamicMin =
    values.length > 0 ? (Math.min(...values) / 1.1).toFixed(2) : 0;
  const dynamicMax =
    values.length > 0 ? (Math.max(...values) * 1.1).toFixed(2) : 100;

  return {
    color: [getThemeColor('primary')], // Keep one line with the primary color
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'line',
        axis: 'x'
      },
      formatter: function (params) {
        const value = params[0].value[1];
        const time = params[0].value[0];
        return `Time: ${dayjs(time).format(
          'HH:mm:ss'
        )}<br/>Value: ${value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: value => dayjs(value).format('HH:mm'),
        interval: 'auto',
        margin: 10
      },
      splitLine: {
        show: true, // Dashed vertical lines along the x-axis
        lineStyle: {
          type: 'dashed',
          color:
            theme === 'dark'
              ? getThemeColor('gray-100')
              : getThemeColor('gray-300')
        }
      }
    },
    yAxis: {
      position: 'right',
      axisPointer: { type: 'none' },
      axisTick: { show: false },
      splitLine: {
        show: false
      },
      axisLine: { show: false },
      axisLabel: { show: true },
      min: dynamicMin,
      max: dynamicMax
    },
    series: [
      {
        type: 'line',
        data: sortedData,
        showSymbol: false,
        symbol: 'circle',
        smooth: true,
        lineStyle: {
          type: 'dashed', // Dashed line style
          width: 2
        }
      }
    ],
    grid: {
      right: 20,
      left: 20,
      bottom: '20px',
      top: '2%',
      containLabel: true
    },
    animation: false,
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: 0,
        end: 100
      }
    ]
  };
};

const MachineTotalProduction = forwardRef(
  ({ bucket, machineName, startTime, endTime, title }, ref) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const chartRef = useRef(null);
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch data from the API based on machineName, startTime, and endTime
        const response = await axios.get(
          `${INFLUX_API}/api/influx/${machineName}/production?start=${startTime}&end=${endTime}&bucket=${bucket}`
        );

        setData(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    const {
      config: { theme },
      getThemeColor
    } = useAppContext();

    const option = getDefaultOptions(theme, getThemeColor, data);

    useImperativeHandle(ref, () => ({
      resize: () => {
        if (chartRef.current) {
          setTimeout(() => {
            const instance = chartRef.current.getEchartsInstance();
            instance.resize();
          }, 200);
        }
      }
    }));

    console.log(
      'Rendering MachineTotalProduction with data:',
      new Date().toISOString()
    );

    useEffect(() => {
      const resizeInterval = setInterval(() => {
        if (chartRef.current) {
          const echartsInstance = chartRef.current.getEchartsInstance();
          echartsInstance.resize();
        }
      }, 1000); // Forțează un resize la fiecare 5 secunde (poți ajusta dacă este necesar)

      return () => clearInterval(resizeInterval);
    }, []);

    useEffect(() => {
      fetchData();
      const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 60000) : null; // Refresh data every minute
      return () => clearInterval(intervalId);
    }, [machineName, startTime, endTime]);

    if (loading) {
      return (
        <Card className="h-0">
          <Card.Body style={{ height: '316px', width: '100%' }}>
            <div style={styles.spinnerContainer}>
              <FallingLines
                color={theme === 'dark' ? 'white' : '#3874ff'}
                width="100"
                visible={true}
                ariaLabel="falling-circles-loading"
              />
            </div>
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card className="h-100">
        <Card.Body>
          <div style={styles.headerContainer}>
            <div style={styles.title}>{title}</div>
          </div>
          <ReactEChartsCore
            ref={node => {
              chartRef.current = node;
              if (ref) ref.current = node; // Asigură forwardRef
            }}
            echarts={echarts}
            option={option}
            style={{ height: '316px', width: '100%' }}
          />
        </Card.Body>
      </Card>
    );
  }
);

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  }
};

export default memo(MachineTotalProduction);
